import { useStripe } from "@stripe/react-stripe-js";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import useCartStore from "../api/cart";
import btnArrow from "../assets/img/section-btn-arrow.webp";
import { adminOrderUrl } from "../utils/adminOrderUrl";
import { resetKioskSession } from "../utils/kioskSession";
import { authHeader } from "../utils/staffAuth";

const Thankyou = () => {
  const today = new Date();
  const formattedDate = `${today.toLocaleString("default", {
    month: "long",
  })} ${today.getDate()}, ${today.getFullYear()}`;
  const { removeFromCart, cartItems } = useCartStore();
  const navigate = useNavigate();
  const stripe = useStripe();
  const orderCreateStarted = useRef(false);

  // The order id lives in React state (rather than being re-read from
  // sessionStorage) so the order can still be (re)fetched after the
  // shared-device session has been wiped below.
  const [orderId, setOrderId] = useState(() =>
    sessionStorage.getItem("orderId"),
  );
  const [pageError, setPageError] = useState(null);
  // Fully discounted orders arrive via navigate() with nothing left to confirm;
  // Stripe redirects carry the client secret and are finalised once the payment
  // callback has been posted.
  const [orderFinalised, setOrderFinalised] = useState(
    () =>
      !new URLSearchParams(window.location.search).get(
        "payment_intent_client_secret",
      ),
  );

  const fetchOrderData = async () => {
    // Public summary route: only what this page shows, never the full order.
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}order/thankyou/${orderId}`,
    );
    const order = response.data?.order;
    if (!order) {
      throw new Error("Order not found");
    }
    return order;
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ["fetchOrderData", orderId],
    queryFn: fetchOrderData,
    enabled: Boolean(orderId),
    retry: 3,
    retryDelay: 2000,
  });

  useEffect(() => {
    if (cartItems && cartItems.length != 0) {
      removeFromCart(cartItems[0]?.id);
      sessionStorage.removeItem("product-store");
      sessionStorage.setItem("thankyoupage", "yes");
    }
    if (sessionStorage.getItem("thankyoupage") != "yes") {
      sessionStorage.removeItem("product-store");
      toast.error("Sign in to see this page.");
      navigate("/");
    }
  }, []);

  useEffect(() => {
    if (!stripe || orderCreateStarted.current) {
      return;
    }

    const clientSecret = new URLSearchParams(window.location.search).get(
      "payment_intent_client_secret",
    );

    if (!clientSecret) {
      return;
    }

    orderCreateStarted.current = true;

    stripe
      .retrievePaymentIntent(clientSecret)
      .then(async ({ paymentIntent }) => {
        if (!paymentIntent) {
          orderCreateStarted.current = false;
          setPageError(
            "We couldn't confirm your payment. Please contact support if you were charged.",
          );
          return;
        }

        const url = new URL(window.location.href);
        url.searchParams.delete("payment_intent_client_secret");
        url.searchParams.delete("payment_intent");
        url.searchParams.delete("redirect_status");
        const cleanUrl = url.pathname + (url.search ? url.search : "");
        window.history.replaceState({}, "", cleanUrl);

        if (paymentIntent?.status !== "succeeded") {
          // Payment didn't go through – nothing to record, let staff start over.
          setPageError(
            "Your payment was not successful. Please try again from checkout.",
          );
          setOrderFinalised(true);
          return;
        }

        const pendingOrderData = sessionStorage.getItem("pendingOrderData");
        let createdOrderId = sessionStorage.getItem("orderId");

        if (pendingOrderData && !createdOrderId) {
          try {
            const orderData = JSON.parse(pendingOrderData);
            // payment_intent_id tags the order with the payment it was
            // created for, so the server's orphan sweep can tell an order
            // this screen started from one it never created.
            const response = await axios.post(
              `${process.env.REACT_APP_API_URL}order/createforpaymentintent`,
              { ...orderData, payment_intent_id: paymentIntent?.id },
              // Who took the order. The server never refuses without it.
              { headers: authHeader() },
            );
            if (response.status === 200) {
              createdOrderId = response?.data?.data?._id;
              sessionStorage.setItem("orderId", createdOrderId);
              sessionStorage.removeItem("pendingOrderData");

              await axios.post(
                `${process.env.REACT_APP_API_URL}order/callbackpaymentintent/${createdOrderId}`,
                {
                  payment_intent: paymentIntent?.id,
                  payment: "paid",
                },
              );
              setOrderId(createdOrderId);
              setOrderFinalised(true);
            } else {
              setPageError(
                "Payment succeeded, but we couldn't create your order. Please contact support.",
              );
            }
          } catch (error) {
            console.error("[THANKYOU] Error creating order:", error);
            setPageError(
              "Payment succeeded, but we couldn't create your order. Please contact support.",
            );
            orderCreateStarted.current = false;
          }
        } else if (createdOrderId) {
          try {
            await axios.post(
              `${process.env.REACT_APP_API_URL}order/callbackpaymentintent/${createdOrderId}`,
              {
                payment_intent: paymentIntent?.id,
                payment: "paid",
              },
            );
            setOrderId(createdOrderId);
            setOrderFinalised(true);
          } catch (error) {
            console.error("[THANKYOU] Error confirming payment:", error);
            setPageError(
              "Your order was placed, but we couldn't finish payment confirmation. Please contact support.",
            );
          }
        }
      })
      .catch((error) => {
        console.error("[THANKYOU] Error retrieving payment intent:", error);
        orderCreateStarted.current = false;
        setPageError(
          "We couldn't confirm your payment. Please contact support if you were charged.",
        );
      });
  }, [stripe]);

  const errorMessage =
    pageError ||
    (isError
      ? "We couldn't load your order details. Please contact support."
      : null);

  // Order complete: forget everything this browser knows about the customer so
  // the next one on the shared device starts clean. The order itself lives in
  // React Query state / the orderId state above, so the page keeps rendering.
  useEffect(() => {
    if (orderFinalised) {
      resetKioskSession();
    }
  }, [orderFinalised]);

  const startNewOrder = () => {
    resetKioskSession();
    window.location.replace("/");
  };

  const startNewOrderButton = (
    <button type="button" className="section-main-btn" onClick={startNewOrder}>
      Start New Order{" "}
      <span>
        <img src={btnArrow} alt="" />
      </span>
    </button>
  );

  return (
    <section className="thankyou-section relative">
      {errorMessage && (
        <div className="container padding-x-all">
          <div className="thankyou-box sec-com-padding">
            <h1>We couldn't complete this page.</h1>
            <p className="thankyou-para">{errorMessage}</p>
            {startNewOrderButton}
          </div>
        </div>
      )}
      {!errorMessage && !isLoading && (data || orderFinalised) && (
        <div className="container padding-x-all">
          <div className="thankyou-box sec-com-padding">
            {data ? (
              <>
                <h1>
                  Thank you for your order
                  {data?.customer_name ? `, ${data.customer_name}` : ""}
                  ! <br />
                  Your order has been placed.
                </h1>
                <p className="thankyou-para">
                  Check your email for your order confirmation.
                </p>
                <p className="thankyou-para">
                  Order {data?.custom_order_id} · {formattedDate}
                </p>
              </>
            ) : (
              <>
                <h1>We couldn't confirm this order.</h1>
                <p className="thankyou-para">
                  Please check the orders list before trying again.
                </p>
              </>
            )}
            <div className="thankyou-actions">
              {data && adminOrderUrl(orderId) && (
                // Opens the order in superadmin in a new tab, so staff can
                // add details and print stickers; this tab keeps Start New Order.
                <a
                  className="thankyou-open-order"
                  href={adminOrderUrl(orderId)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open order {data?.custom_order_id}
                </a>
              )}
              {orderFinalised && startNewOrderButton}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Thankyou;
